import { useEffect } from 'react';
import { useShadowRootElements } from '@backstage/plugin-techdocs-react';
import { Theme, useTheme } from '@material-ui/core';

const getTechdocsCSS = (theme: Theme) => {
  // Use MUI's body1 font size so TechDocs text is consistent with paragraph
  // text in the rest of Varia (1rem / 16px).
  const bodyFontSize = theme.typography.body1.fontSize as string;

  return `
    body {
      font-family: ${theme.typography.fontFamily};
      color: ${theme.palette.text.primary};
      font-size: ${bodyFontSize};

      --md-typeset-a-color:   ${theme.palette.primary.main};
      --md-typeset-font-size: ${bodyFontSize};
      --md-typeset-color:     ${theme.palette.text.primary};
      --md-typeset-a-color:   ${theme.palette.primary.main};

      /* Primary color — drives header, sidebar, links, active states natively */
      --md-primary-fg-color:        ${theme.palette.primary.main};
      --md-primary-fg-color--light: ${theme.palette.primary.light};
      --md-primary-fg-color--dark:  ${theme.palette.primary.dark};
      --md-primary-bg-color:        ${theme.palette.primary.contrastText};
      --md-primary-bg-color--light: ${theme.palette.primary.contrastText};

      /* Accent color — drives hover states, scrollbar, buttons */
      --md-accent-fg-color: ${theme.palette.secondary.main};

      /* Default foreground/background */
      --md-default-fg-color: ${theme.palette.text.primary};
      --md-default-bg-color: ${theme.palette.background.default};

      /* Code blocks */
      --md-code-fg-color: ${theme.palette.text.primary};
      --md-code-bg-color: ${theme.palette.background.paper};

      /* Footer */
      --md-footer-fg-color: ${theme.palette.text.primary};
      --md-footer-bg-color: ${theme.palette.background.paper};

      /* Admonitions (note/warning/tip boxes) */
      --md-admonition-bg-color: ${theme.palette.background.paper};

      /* Breadcrumb separator icon (chevron-right), used as a CSS mask-image */
      --md-path-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.42z'/%3E%3C/svg%3E");
    }

    a,
    a:link,
    a:visited,
    a:hover {
      color: ${theme.palette.primary.main};
    }

    .md-tabs {
      background-color: ${theme.palette.background.paper};
      box-shadow: ${theme.shadows[1]};
      border-radius: 4px;
    }

    .md-tabs__link {
      color: ${theme.palette.primary.main};

      &:hover {
        color: ${theme.palette.primary.main};
        text-decoration: underline;
      }
    }

    .md-tabs__item--active .md-tabs__link {
      color: ${theme.palette.primary.main};
      font-weight: bold;
    }

    .md-nav__item {
      font-size: var(--md-typeset-font-size);
    }

    .md-nav__item--nested {
      color: ${theme.palette.primary.main};
    }

    /* Compensate for footer */
    article.md-content__inner {
      padding-bottom: 3em;
    }
  `;
};

export const AddTechdocsStyling = () => {
  const theme = useTheme();

  const headTag = useShadowRootElements<HTMLHeadElement>(['head']);
  const bodyTag = useShadowRootElements<HTMLBodyElement>(['body']);

  useEffect(() => {
    // Add a <style> tag with custom CSS based on Varia theme to the <head>
    // of the embedded techdocs
    if (headTag && headTag.length > 0) {
      let styleElement = headTag[0].querySelector('#varia-techdocs-styles') as HTMLStyleElement;

      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.setAttribute('id', 'varia-techdocs-styles');
        headTag[0].appendChild(styleElement);
      }

      styleElement.textContent = getTechdocsCSS(theme);
    }

    // Add an identifying attribute to the <body> tag to allow for
    // scoped styling by external teams
    if (bodyTag && bodyTag.length > 0) {
      bodyTag[0].setAttribute('data-varia-techdocs', 'true');
    }
  }, [bodyTag, headTag, theme]);

  return null;
};
